import React from "react";
import { View, StyleSheet } from "react-native";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import BaseController from "../../common/base/BaseController";
import NtPageSection from "../../components/NtPageSection";
import NtSectionLabel from "../../components/NtSectionLabel";
import NtStatCard from "../../components/NtStatCard";
import NtListHeader from "../../components/NtListHeader";
import NtListRow from "../../components/NtListRow";
import NtBadge from "../../components/NtBadge";
import NtPageActionButton from "../../components/NtPageActionButton";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import NtText from "../../components/NtText";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";

import NtCdrVolumeChart from "../components/NtCdrVolumeChart";
import NtRegistrationsHealth from "../components/NtRegistrationsHealth";
import NtFootprintGrid from "../components/NtFootprintGrid";

import pinstatisticcolumndata from "../data/pinstatisticcolumndata";
import cdrstatisticcolumndata from "../data/cdrstatisticcolumndata";
import recentpincdrcolumndata from "../data/recentpincdrcolumndata";

import { fetchStatistics } from "../../api/dashboard";
import { fetchExtensions, fetchRegistrations } from "../../api/extension";
import { fetchRingGroups } from "../../api/ringgroup";
import { fetchInboundRoutes } from "../../api/inboundroute";
import { fetchVoicemailBox } from "../../api/voicemail";
import { fetchRecordings } from "../../api/recording";
import { fetchIvrMenus } from "../../api/ivr";
import { fetchUsers } from "../../api/user";
import { fetchCdrs } from "../../api/billing";

import {
  parseDecimalPlaces,
  parseDisplayDate,
} from "../../common/utils/ControllerUtils";
import Logger from "../../common/utils/Logger";
import { useTheme } from "../../context/ThemeContext";
import { fetchCurrency } from "../../vvn/utils/CurrencyParser";
import routes from "../../navigation/routes";
import settings from "../../config/settings";

/**
 * Bold "total" cell text — themed and matched to NtListRow's cell font size
 * so the total row sits flush with the other rows above it. Hook-only so it
 * can read `theme` from context (the surrounding controller is a class).
 */
function TotalText({ children }) {
  const { theme } = useTheme();
  return (
    <NtText style={{ fontSize: 12, fontWeight: "700", color: theme.text }}>
      {children}
    </NtText>
  );
}

function AlertBar({ offlineCount, total, onPress }) {
  const { theme } = useTheme();
  if (!offlineCount) return null;
  const pct = total > 0 ? ((offlineCount / total) * 100).toFixed(1) : "0";
  const tone = offlineCount > total * 0.25 ? "red" : "yellow";
  const color = tone === "red" ? theme.red : theme.yellow;
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: theme.radius,
        backgroundColor: color + "14",
        borderWidth: 1,
        borderColor: color + "38",
        marginTop: 14,
      }}
    >
      {/* alignSelf override is needed because NtBadge defaults to flex-start
          which would otherwise pin it to the top of the row. */}
      <NtBadge variant={tone} style={{ alignSelf: "center", marginRight: 10 }}>
        {tone === "red" ? "Critical" : "Warning"}
      </NtBadge>
      <NtText style={{ color: theme.text, fontSize: 12, fontWeight: "700" }}>
        {offlineCount} extension{offlineCount === 1 ? "" : "s"} offline
      </NtText>
      <NtText style={{ color: theme.textDim, fontSize: 12, marginLeft: 8 }}>
        · {pct}% of fleet not registered
      </NtText>
      <View style={{ flex: 1 }} />
      <NtPageActionButton title="View Registrations" onPress={onPress} icon="arrow-right" />
    </View>
  );
}

// Quick-action shortcuts to the common create-flows. Each item routes to its
// section (where the "add" affordance lives). Hook child so it can read theme.
function QuickActions({ actions }) {
  const { theme } = useTheme();
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
      {actions.map((a) => (
        <NtTouchableEffect
          key={a.key}
          onPress={a.onPress}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            paddingHorizontal: 14,
            paddingVertical: 10,
            borderRadius: theme.radius,
            borderWidth: 1,
            borderColor: theme.border,
            backgroundColor: theme.surface3,
          }}
        >
          <NtMaterialIcon name={a.icon} color={theme.brand2} size={15} />
          <NtText style={{ color: theme.text, fontSize: 12.5, fontWeight: "600" }}>
            {a.label}
          </NtText>
        </NtTouchableEffect>
      ))}
    </View>
  );
}

class DashboardController extends BaseController {
  state = {
    loading: false,
    statistics: null,
    registrations: [],
    cdrs24h: [],
    recentCdrs: [],
    footprint: {},
  };

  componentDidMount() {
    this.didFocusListener = this.props.navigation.addListener(
      "focus",
      this.handleFocus,
    );
    this.handleDataFetch();
  }

  componentWillUnmount() { this.didFocusListener?.(); }
  handleFocus = () => Logger("Dashboard focus");

  handleDataFetch = async () => {
    const domain = this.context.user?.domain;
    if (!domain) return;
    this.setState({ loading: true });

    const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

    // Calling-card data (PIN statistics + CDRs) is only fetched when the
    // feature is enabled — no point hitting those endpoints for tenants that
    // don't use calling cards. Resolve to falsy so the setState below no-ops.
    const noop = Promise.resolve(null);
    const cc = settings.callingCardsEnabled;

    // Primary data — single parallel batch.
    const [
      statsRes,
      regsRes,
      extRes,
      cdrs24hRes,
      recentRes,
    ] = await Promise.all([
      cc ? fetchStatistics(domain) : noop,
      fetchRegistrations(domain),
      fetchExtensions(domain, 1, 1, null, null),
      cc ? fetchCdrs(1, 1000, null, { dateFrom: dayAgo }) : noop,
      cc ? fetchCdrs(1, 5, null, null) : noop,
    ]);
    Logger("Dashboard statistics", statsRes);
    Logger("Dashboard registrations", regsRes);
    Logger("Dashboard extensions count", extRes);
    Logger("Dashboard cdrs 24h", cdrs24hRes);
    Logger("Dashboard recent cdrs", recentRes);

    // Footprint counts — second parallel batch (each is ?pagesize=1).
    const [
      rgRes,
      ivrRes,
      irRes,
      vmRes,
      recRes,
      userRes,
    ] = await Promise.all([
      fetchRingGroups(domain, 1, 1, null, null),
      fetchIvrMenus(domain, 1, 1, null),
      fetchInboundRoutes(domain, 1, 1, null),
      fetchVoicemailBox(domain, "all", 1, 1, null, null),
      fetchRecordings(domain, 1, 1, null),
      fetchUsers(domain, 1, 1, null, null),
    ]);
    Logger("Dashboard footprint", { rgRes, ivrRes, irRes, vmRes, recRes, userRes });

    const totalOf = (res) => (res?.ok ? res.data?.total ?? 0 : 0);

    this.setState({
      loading: false,
      statistics: statsRes?.ok ? statsRes.data : null,
      registrations: regsRes.ok ? regsRes.data || [] : [],
      cdrs24h: cdrs24hRes?.ok ? cdrs24hRes.data?.data || [] : [],
      recentCdrs: recentRes?.ok ? recentRes.data?.data || [] : [],
      footprint: {
        extensions: totalOf(extRes),
        ringGroups: totalOf(rgRes),
        ivrs: totalOf(ivrRes),
        inboundRoutes: totalOf(irRes),
        voicemails: totalOf(vmRes),
        recordings: totalOf(recRes),
        users: totalOf(userRes),
      },
    });
  };

  computeKpis = () => {
    const s = this.state.statistics;
    const cc = s?.callingcards || [];
    const totalRow = cc.find((c) => c.type === "total");
    const activeCalls = cc
      .filter((c) => c.type !== "total")
      .reduce((sum, c) => sum + (c.calls_current || 0), 0);
    const usedMinutes =
      totalRow?.used_minutes ??
      cc
        .filter((c) => c.type !== "total")
        .reduce((sum, c) => sum + (c.used_minutes || 0), 0);
    const sessionCost = totalRow?.sessioncost ?? 0;
    const sessionPrice = totalRow?.sessionprice ?? 0;
    const margin = (sessionPrice || 0) - (sessionCost || 0);
    return { activeCalls, usedMinutes, sessionCost, sessionPrice, margin };
  };

  computeOfflineCount = () => {
    const total = this.state.footprint?.extensions || 0;
    const reg = this.state.registrations?.length || 0;
    return Math.max(0, total - reg);
  };

  renderCallingCardsTable = () => {
    const cc = this.state.statistics?.callingcards;
    if (!cc?.length) return null;
    return (
      <NtPageSection style={{ marginTop: 4 }}>
        <View style={styles.tableWrap}>
          <NtListHeader selectedColumns={pinstatisticcolumndata} />
          {cc.filter((each) => each.type).map((each) => (
            <NtListRow
              key={`cc-${each.type}`}
              item={each}
              columns={pinstatisticcolumndata}
              renderCustom={(row, column) => {
                if (column === "type" && row.type === "total") {
                  return (
                    <TotalText>
                      {row.type[0]?.toUpperCase() + row.type?.slice(1)}
                    </TotalText>
                  );
                }
                if (column === "status" && row.type !== "total") {
                  const active = row[column] === 1;
                  return (
                    <NtBadge variant={active ? "green" : "gray"}>
                      {active ? "Active" : "Inactive"}
                    </NtBadge>
                  );
                }
                if (row.type === "total") {
                  return <TotalText>{row[column]}</TotalText>;
                }
              }}
            />
          ))}
        </View>
      </NtPageSection>
    );
  };

  renderCallingCardsCdrsTable = () => {
    const cc = this.state.statistics?.callingcards;
    if (!cc?.length) return null;
    return (
      <NtPageSection style={{ marginTop: 4 }}>
        <View style={styles.tableWrap}>
          <NtListHeader selectedColumns={cdrstatisticcolumndata} />
          {cc.map((each) => (
            <NtListRow
              key={`cccdr-${each.type}`}
              item={each}
              columns={cdrstatisticcolumndata}
              renderCustom={(row, column) => {
                if (column === "free_minutes_given") {
                  const free = (row.used_minutes || 0) - (row.billed_minutes || 0);
                  if (row.type === "total") return <TotalText>{free}</TotalText>;
                  return free;
                }
                if (column === "sessioncost" || column === "sessionprice") {
                  const text = `$${parseDecimalPlaces(row[column])}`;
                  if (row.type === "total") return <TotalText>{text}</TotalText>;
                  return text;
                }
                if (column === "type" && row.type === "total") {
                  return (
                    <TotalText>
                      {row.type[0]?.toUpperCase() + row.type?.slice(1)}
                    </TotalText>
                  );
                }
                if (row.type === "total") {
                  return <TotalText>{row[column]}</TotalText>;
                }
              }}
            />
          ))}
        </View>
      </NtPageSection>
    );
  };

  renderRecentCallsTable = () => {
    return (
      <View style={styles.tableWrap}>
        <NtListHeader selectedColumns={recentpincdrcolumndata} />
        {this.state.recentCdrs.length === 0 ? (
          <NtText style={styles.emptyText}>No recent PIN calls.</NtText>
        ) : null}
        {this.state.recentCdrs.map((row, idx) => (
          <NtListRow
            key={`recent-${row.callid || row.id || idx}`}
            item={row}
            columns={recentpincdrcolumndata}
            renderCustom={(item, column) => {
              if (column === "starttime") {
                return parseDisplayDate(item[column], "HH:mm:ss");
              }
              if (column === "sessiontime") {
                const s = parseFloat(item[column]) || 0;
                const m = Math.floor(s / 60);
                const r = Math.floor(s % 60).toString().padStart(2, "0");
                return `${m}:${r}`;
              }
              if (column === "sessionprice") {
                const c = fetchCurrency(item.currency);
                return `${c?.symbol || "$"}${parseDecimalPlaces(item[column])}`;
              }
            }}
          />
        ))}
      </View>
    );
  };

  render() {
    const { loading, statistics, registrations, cdrs24h, footprint } = this.state;
    const k = this.computeKpis();
    const offline = this.computeOfflineCount();

    const subtitle = `Operational view for ${this.context.user?.domain || "your tenant"}`;
    const showCallingCards = settings.callingCardsEnabled;

    const go = (route) => this.props.navigation?.navigate?.(route);
    const quickActions = [
      { key: "ext", label: "New extension", icon: "phone", onPress: () => go(routes.extension) },
      { key: "rg", label: "New ring group", icon: "account-group", onPress: () => go(routes.ringgroup) },
      { key: "ivr", label: "Set up IVR", icon: "menu", onPress: () => go(routes.ivr) },
      { key: "num", label: "Add number", icon: "phone-plus-outline", onPress: () => go(routes.number) },
      { key: "user", label: "Invite user", icon: "account-circle", onPress: () => go(routes.user) },
    ];

    const footprintItems = [
      { label: "Extensions", value: footprint.extensions },
      { label: "Ring Groups", value: footprint.ringGroups },
      { label: "IVR Menus", value: footprint.ivrs },
      { label: "Inbound Routes", value: footprint.inboundRoutes },
      { label: "Voicemails", value: footprint.voicemails },
      { label: "Recordings", value: footprint.recordings },
      { label: "Users", value: footprint.users },
    ];

    return (
      <NtRootPageContainer pageTitle="Dashboard" pageSubtitle={subtitle}>
        {/* General PBX KPIs — always shown, from footprint + registrations. */}
        <View style={styles.kpiRow}>
          <NtStatCard
            label="Extensions Online"
            value={`${registrations.length} / ${footprint.extensions || 0}`}
            delta={
              footprint.extensions
                ? `${((registrations.length / footprint.extensions) * 100).toFixed(1)}% registered`
                : null
            }
            trend="up"
          />
          <NtStatCard label="Users" value={footprint.users || 0} />
          <NtStatCard label="Ring Groups" value={footprint.ringGroups || 0} />
          <NtStatCard label="IVR Menus" value={footprint.ivrs || 0} />
          <NtStatCard label="Inbound Routes" value={footprint.inboundRoutes || 0} />
        </View>

        {/* Calling-card KPIs — only when the module is enabled. */}
        {showCallingCards && (
          <View style={[styles.kpiRow, { marginTop: 12 }]}>
            <NtStatCard label="Active PIN Calls" value={k.activeCalls} />
            <NtStatCard
              label="PIN Minutes Used"
              value={(k.usedMinutes || 0).toLocaleString()}
              delta={`$${parseDecimalPlaces(k.sessionCost || 0)} cost`}
            />
            <NtStatCard
              label="PIN Margin (today)"
              value={`$${parseDecimalPlaces(k.margin || 0)}`}
              delta={`$${parseDecimalPlaces(k.sessionPrice || 0)} billed`}
              trend={k.margin > 0 ? "up" : k.margin < 0 ? "down" : null}
            />
          </View>
        )}

        <NtSectionLabel>Quick Actions</NtSectionLabel>
        <View style={styles.qaCard}>
          <QuickActions actions={quickActions} />
        </View>

        {loading && !statistics ? (
          <NtActivityIndicator containerStyle={{ marginTop: 20 }} />
        ) : null}

        <View style={styles.twoCol}>
          {showCallingCards && (
            <NtPageSection
              title="Calling Cards · CDR Volume (24h)"
              style={{ flex: 2 }}
              bodyStyle={styles.flexBody}
            >
              <NtCdrVolumeChart cdrs={cdrs24h} />
            </NtPageSection>
          )}
          <NtPageSection
            title="Registrations Health"
            style={{ flex: 1 }}
            bodyStyle={styles.flexBody}
          >
            <NtRegistrationsHealth
              registrations={registrations}
              extensionsTotal={footprint.extensions || 0}
            />
          </NtPageSection>
        </View>

        <AlertBar
          offlineCount={offline}
          total={footprint.extensions || 0}
          onPress={() => this.props.navigation?.navigate?.(routes.registration)}
        />

        {showCallingCards && (
          <>
            <NtSectionLabel>Calling Cards</NtSectionLabel>
            {this.renderCallingCardsTable()}

            <NtSectionLabel>Calling Cards · CDRs</NtSectionLabel>
            {this.renderCallingCardsCdrsTable()}
          </>
        )}

        <NtSectionLabel>Activity & Inventory</NtSectionLabel>
        <View style={styles.twoColEq}>
          {showCallingCards && (
            <NtPageSection
              title="Recent PIN Calls"
              style={{ flex: 1 }}
              bodyStyle={styles.flexBody}
            >
              {this.renderRecentCallsTable()}
            </NtPageSection>
          )}
          <NtPageSection
            title="System Footprint"
            style={{ flex: 1 }}
            bodyStyle={styles.flexBody}
          >
            <NtFootprintGrid items={footprintItems} />
          </NtPageSection>
        </View>
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  kpiRow: { flexDirection: "row", flexWrap: "wrap", gap: 12, marginBottom: 4 },
  qaCard: {
    backgroundColor: "transparent",
    marginBottom: 4,
  },
  twoCol: { flexDirection: "row", gap: 14, marginTop: 14, alignItems: "stretch" },
  twoColEq: { flexDirection: "row", gap: 14, alignItems: "stretch" },
  // Lets the body fill any extra height the row gives the card so siblings
  // stay equal-height instead of sizing to their own content.
  flexBody: { flex: 1 },
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
  emptyText: {
    paddingVertical: 14,
    fontSize: 12,
    fontStyle: "italic",
    textAlign: "center",
  },
});

export default DashboardController;
